import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button, DateTimeInput, SpotPicker, useConfirm } from "@/ui";
import {
  useNotifyTournamentUpdate,
  useSpots,
  useTournament,
  useUpdateTournament,
  type SeedingMode,
  type TournamentFormat as TFormat,
} from "@/features/queries";
import { noAutofill } from "@/lib/noAutofill";

export function AdminTournamentEditPage() {
  const { id: idStr } = useParams();
  const tournamentId = Number(idStr);
  const navigate = useNavigate();

  const { data: t, isLoading } = useTournament(tournamentId);
  const { data: spots = [] } = useSpots();
  const updateMut = useUpdateTournament();
  const notifyMut = useNotifyTournamentUpdate();
  const confirm = useConfirm();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [spotId, setSpotId] = useState<number | null>(null);
  const [dateTime, setDateTime] = useState<string | null>(null);
  const [maxParticipants, setMaxParticipants] = useState("16");
  const [entryFee, setEntryFee] = useState("0");
  const [format, setFormat] = useState<TFormat>("single_elim");
  const [seeding, setSeeding] = useState<SeedingMode>("rating");
  const [bestOf, setBestOf] = useState("3");

  useEffect(() => {
    if (!t) return;
    setName(t.name);
    setDescription(t.description ?? "");
    setSpotId(t.spot_id);
    setDateTime(t.date_time);
    setMaxParticipants(String(t.max_participants));
    setEntryFee(String(t.entry_fee));
    setFormat(t.format);
    setSeeding(t.seeding_mode);
    setBestOf(String(t.best_of));
  }, [t]);

  if (isLoading || !t) {
    return (
      <div>
        <BackLink onClick={() => navigate(`/admin/tournaments/${tournamentId}`)} />
        <p className="body">Загрузка…</p>
      </div>
    );
  }

  const locked = t.status !== "draft" && t.status !== "registration";
  const formatLocked = locked; // не меняем формат у стартовавшего

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (spotId == null || !dateTime) return;
    try {
      await updateMut.mutateAsync({
        id: tournamentId,
        patch: {
          name: name.trim(),
          description: description.trim() || null,
          spot_id: spotId,
          date_time: dateTime,
          max_participants: Number(maxParticipants),
          entry_fee: Number(entryFee),
          format,
          seeding_mode: seeding,
          best_of: Number(bestOf),
        },
      });
      const notify = await confirm({
        title: "Предупредить участников об изменениях?",
        description:
          "Всем записавшимся уйдёт уведомление в Telegram с обновлённой информацией о турнире.",
        confirmLabel: "Да, отправить",
        cancelLabel: "Не надо",
      });
      if (notify) {
        try {
          await notifyMut.mutateAsync(tournamentId);
        } catch {
          /* notification failure doesn't block navigation */
        }
      }
      navigate(`/admin/tournaments/${tournamentId}`);
    } catch {
      /* error shown below */
    }
  };

  return (
    <>
      <BackLink onClick={() => navigate(`/admin/tournaments/${tournamentId}`)} />

      <h1 className="h1" style={{ marginBottom: 16 }}>Редактирование турнира</h1>

      {locked && (
        <div style={{
          background: "rgba(255,184,0,0.15)", color: "#8A5A00",
          padding: "10px 14px", borderRadius: "var(--r-md)",
          fontSize: 13, marginBottom: 16,
        }}>
          Турнир уже стартовал. Можно менять описание и дату, но формат и посев изменить нельзя.
        </div>
      )}

      <form
        onSubmit={onSubmit}
        style={{
          background: "var(--bg-section)",
          borderRadius: "var(--r-lg)",
          padding: 24,
          maxWidth: 640,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <FormField label="Название">
          <TextInput value={name} onChange={setName} required />
        </FormField>

        <FormField label="Описание">
          <TextArea value={description} onChange={setDescription} rows={3} />
        </FormField>

        <FormField label="Площадка">
          <SpotPicker spots={spots} value={spotId} onChange={setSpotId} />
        </FormField>

        <FormField label="Дата и время">
          <DateTimeInput value={dateTime} onChange={setDateTime} required />
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <FormField label="Макс. участников">
            <TextInput type="number" value={maxParticipants} onChange={setMaxParticipants} min={2} max={256} />
          </FormField>
          <FormField label="Взнос, ₽">
            <TextInput type="number" value={entryFee} onChange={setEntryFee} min={0} />
          </FormField>
        </div>

        <FormField label="Формат сетки">
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as TFormat)}
            disabled={formatLocked}
            style={inputStyle}
          >
            <option value="single_elim">Single-elimination</option>
            <option value="double_elim">Double-elimination</option>
            <option value="round_robin">Round-robin</option>
          </select>
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <FormField label="Посев">
            <select
              value={seeding}
              onChange={(e) => setSeeding(e.target.value as SeedingMode)}
              disabled={formatLocked}
              style={inputStyle}
            >
              <option value="rating">По рейтингу</option>
              <option value="random">Случайно</option>
              <option value="manual">Вручную</option>
            </select>
          </FormField>
          <FormField label="До победы в">
            <select
              value={bestOf}
              onChange={(e) => setBestOf(e.target.value)}
              disabled={formatLocked}
              style={inputStyle}
            >
              <option value="1">1 игре</option>
              <option value="3">3 играх</option>
              <option value="5">5 играх</option>
              <option value="7">7 играх</option>
            </select>
          </FormField>
        </div>

        {updateMut.error && (
          <div style={{
            background: "rgba(223,63,64,0.1)", color: "var(--fg-danger)",
            padding: "8px 12px", borderRadius: "var(--r-md)", fontSize: 13,
          }}>
            {(updateMut.error as Error).message}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button type="submit" disabled={updateMut.isPending || !name || spotId == null || !dateTime}>
            {updateMut.isPending ? "Сохраняем…" : "Сохранить"}
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/admin/tournaments/${tournamentId}`)}>
            Отмена
          </Button>
        </div>
      </form>
    </>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent", border: 0, color: "var(--fg2)", cursor: "pointer",
        display: "inline-flex", alignItems: "center", gap: 6, padding: 0, marginBottom: 12,
        fontFamily: "var(--font-body)", fontSize: 13,
      }}
    >
      <ArrowLeft size={14} /> назад
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "10px 14px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--fg1)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// <div>, not <label>: composite pickers (SpotPicker) render a trigger button
// plus a search input; a surrounding <label> delegates clicks to the first
// focusable child, which in Yandex/some Chromium variants swallows clicks
// on picker rows.
function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg2)" }}>{label}</span>
      {children}
    </div>
  );
}

function TextInput({
  value, onChange, type = "text", min, max, required,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  min?: number;
  max?: number;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      min={min}
      max={max}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
      {...noAutofill}
    />
  );
}

function TextArea({
  value, onChange, rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, resize: "vertical", fontFamily: "var(--font-body)" }}
    />
  );
}
